import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import VERSION from '../version';

/**
 * WhatsNew popup — changelogData.js'ten beslenir.
 * En son major sürüm + varsa son patch'in özetini gösterir.
 * Kullanıcı "Anladım" deyince VERSION localStorage'a yazılır.
 */
const WhatsNew = () => {
  const [open, setOpen] = useState(false);
  const [VERSION_GROUPS, setGruplar] = useState(null); // V71.39 — notlar (≈240 KB) yalnız pencere açılacaksa yüklenir
  const navigate = useNavigate();

  useEffect(() => {
    const seen = localStorage.getItem('whatsNewSeen') || '';
    // Sadece major.minor değişikliğinde popup göster (patch'lerde gösterme)
    // V20.4 → "20.4", V20.4.1 → "20.4"
    const getMajorMinor = (v) => {
      const m = v.replace(/^V/i, '').match(/^(\d+\.\d+)/);
      return m ? m[1] : v;
    };
    if (getMajorMinor(seen) === getMajorMinor(VERSION)) return undefined;
    let iptal = false;
    import('../changelogData').then(m => { if (!iptal) { setGruplar(m.default); setOpen(true); } }).catch(() => {});
    return () => { iptal = true; };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line
  }, [open]);

  const handleClose = () => {
    localStorage.setItem('whatsNewSeen', VERSION);
    setOpen(false);
  };

  if (!open || !VERSION_GROUPS) return null;

  // VERSION'a karşılık gelen patch veya major'u bul
  const majorGroup = VERSION_GROUPS[0];
  if (!majorGroup) return null;

  // VERSION patch mi (V20.8) yoksa major mu (V20)?
  let display = { ver: majorGroup.major, title: majorGroup.title, date: majorGroup.date, items: majorGroup.items, hr: majorGroup.hr };
  if (majorGroup.patches && majorGroup.patches.length > 0) {
    // Son patch'i veya VERSION'a uyan patch'i göster
    // Z sürümünün (V71.3.1) kendi notu yoksa bağlı olduğu X.Y notu (V71.3) esas alınır
    const matchPatch = majorGroup.patches.find(p => p.ver === VERSION)
      || majorGroup.patches.find(p => VERSION.startsWith(`${p.ver}.`));
    // Sessiz yayın: X.Y sürümünün notu yoksa popup gösterilmez, görüldü işaretlenir
    if (!matchPatch && VERSION !== majorGroup.major) {
      localStorage.setItem('whatsNewSeen', VERSION);
      return null;
    }
    const lastPatch = majorGroup.patches[majorGroup.patches.length - 1];
    const patch = matchPatch || lastPatch;
    if (patch) {
      display = { ver: patch.ver, title: patch.title, date: patch.date, items: patch.items, hr: patch.hr };
    }
  }

  // İK ile ilgili (hr: true işaretli) sürüm notları popup olarak GÖSTERİLMEZ —
  // personelle ilgili içerik tüm kullanıcıların karşısına çıkmamalı.
  // Sessizce "görüldü" işaretlenir ki eski bir İK notu sonraki sürümlerde de belirmesin.
  if (display.hr) {
    if (localStorage.getItem('whatsNewSeen') !== VERSION) localStorage.setItem('whatsNewSeen', VERSION);
    return null;
  }

  // items: string | {emoji, text} — her ikisini de destekle
  const normalize = (item) =>
    typeof item === 'string' ? { emoji: '✨', text: item } : { emoji: item.emoji || '✨', text: item.text };

  const shown = display.items.slice(0, 7).map(normalize);

  return (
    <div style={styles.overlay} onClick={handleClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Neler Yeni?">
        <div style={styles.header}>
          <div>
            <div style={styles.badge}>{display.ver}</div>
            <h2 style={styles.title}>Neler Yeni?</h2>
            <p style={styles.subtitle}>{display.title} — {display.date}</p>
          </div>
          <button onClick={handleClose} style={styles.closeBtn} aria-label="Kapat">✕</button>
        </div>
        <div style={styles.body}>
          {shown.map((item, i) => (
            <div key={i} style={styles.item}>
              <span style={styles.emoji}>{item.emoji}</span>
              <span style={styles.text}>{item.text}</span>
            </div>
          ))}
          {display.items.length > 7 && (
            <div style={{ fontSize: 12, color: 'var(--fg-tertiary)', marginTop: 4 }}>
              +{display.items.length - 7} daha…
            </div>
          )}
        </div>
        <div style={styles.footer}>
          {/* V71 — değişiklik geçmişine doğrudan geçiş */}
          <button className="btn btn-ghost btn-sm" onClick={() => { handleClose(); navigate('/changelog'); }}>Tüm değişiklikler →</button>
          <button className="btn btn-brand" onClick={handleClose} style={{ padding: '10px 32px' }}>Anladım</button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'var(--bg-overlay, rgba(0,0,0,0.45))', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 99999, animation: 'fadeIn .2s ease',
  },
  modal: {
    background: 'var(--bg-elevated)', color: 'var(--fg-primary)', borderRadius: 16, width: 440, maxWidth: '92vw',
    boxShadow: 'var(--shadow-xl)', overflow: 'hidden',
    animation: 'slideUp .3s ease',
  },
  header: {
    background: 'var(--grad-brand)',
    color: '#fff', padding: '24px 28px 18px', display: 'flex',
    justifyContent: 'space-between', alignItems: 'flex-start',
  },
  badge: {
    display: 'inline-block', background: 'rgba(255,255,255,0.2)',
    borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 700,
    letterSpacing: 0.5, marginBottom: 6,
  },
  title: { margin: 0, fontSize: 22, fontWeight: 700 },
  subtitle: { margin: '4px 0 0', fontSize: 13, opacity: 0.8 },
  closeBtn: {
    background: 'none', border: 'none', color: '#fff', fontSize: 20,
    cursor: 'pointer', opacity: 0.7, padding: 4, lineHeight: 1,
  },
  body: { padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 10 },
  item: { display: 'flex', alignItems: 'flex-start', gap: 10 },
  emoji: { fontSize: 18, lineHeight: '24px', flexShrink: 0 },
  text: { fontSize: 14, color: 'var(--fg-primary)', lineHeight: '22px' },
  footer: { padding: '12px 28px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
};

export default WhatsNew;
